import {
  CancelledError,
  type QueryClient,
  queryOptions,
  replaceEqualDeep,
} from "@tanstack/react-query";

import { projectCancelledAskDecodaEntry } from "@/hooks/ask-decoda/ask-decoda-cancelled-presentation";

import { mergeFetchedHistory } from "@/lib/ask-decoda-history-cache";

import type {
  AskDecodaProductMetadata,
  ChatHistoryEntry,
} from "@/types/chat-thread";

import {
  ASK_DECODA_THREAD_DETAIL_STALE_MS,
  askDecodaProductMetadataQueryKey,
  askDecodaThreadHistoryQueryKey,
  getNativeAskDecodaHistory,
} from "@/api/ai/chat-threads";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";

type Owner = { runId: string | undefined; isCurrent: () => boolean };
type ScopeParams = {
  queryClient: QueryClient;
  tenantSubDomain: string | undefined;
  threadId: string;
};
type Ticket = {
  scope: Scope;
  epoch: number;
  revision: number;
  admission: string | undefined;
  owner?: Owner;
  signal: AbortSignal;
};
type Scope = {
  revision: number;
  queryClient: QueryClient;
  tenantSubDomain: string | undefined;
  threadId: string;
  structuralSharing: (previous: unknown, incoming: unknown) => unknown;
};

// Identity-only, client-local concurrency bookkeeping. No transcript copies or
// checkpoint ordering. The weak tag distinguishes REST from manual UI writes.
const scopes = new WeakMap<QueryClient, Map<string, Scope>>();
const readTickets = new WeakMap<ChatHistoryEntry[], Ticket>();

function historyScope({
  queryClient,
  tenantSubDomain,
  threadId,
}: ScopeParams): Scope {
  let clientScopes = scopes.get(queryClient);
  if (!clientScopes) {
    clientScopes = new Map();
    scopes.set(queryClient, clientScopes);
  }
  const key = JSON.stringify(
    askDecodaThreadHistoryQueryKey(tenantSubDomain, threadId),
  );
  let scope = clientScopes.get(key);
  if (!scope) {
    const created: Scope = {
      revision: 0,
      queryClient,
      tenantSubDomain,
      threadId,
      structuralSharing: (previous, incoming) => {
        const ticket = Array.isArray(incoming)
          ? readTickets.get(incoming)
          : undefined;
        if (!ticket) return replaceEqualDeep(previous, incoming);
        if (!eligible(created, threadId, ticket)) {
          if (previous === undefined)
            throw new CancelledError({ revert: false });
          return previous;
        }
        const fetched = incoming as ChatHistoryEntry[];
        const metadataKey = askDecodaProductMetadataQueryKey(
          tenantSubDomain,
          threadId,
        );
        const currentMetadata =
          queryClient.getQueryData<AskDecodaProductMetadata>(metadataKey) ?? {};
        const nextMetadata = { ...currentMetadata };
        for (const entry of fetched) {
          const prior = currentMetadata[entry.id];
          nextMetadata[entry.id] = prior?.pendingFeedback
            ? prior
            : {
                feedbackType: entry.feedbackType ?? null,
                feedbackText: entry.feedbackText ?? null,
              };
        }
        queryClient.setQueryData(metadataKey, nextMetadata);
        const transcript = fetched.map((entry) => ({
          ...projectCancelledAskDecodaEntry(tenantSubDomain, entry),
          feedbackType: null,
          feedbackText: null,
          pendingFeedback: undefined,
        }));
        return replaceEqualDeep(
          previous,
          mergeFetchedHistory(
            previous as ChatHistoryEntry[] | undefined,
            transcript,
          ),
        );
      },
    };
    scope = created;
    clientScopes.set(key, scope);
  }
  return scope;
}

function eligible(scope: Scope, threadId: string, ticket: Ticket): boolean {
  const state = useAskDecodaStore.getState();
  const admission = state.liveRunsByThreadId[threadId]?.runId;
  return (
    ticket.scope === scope &&
    !ticket.signal.aborted &&
    state.runtimeEpoch === ticket.epoch &&
    scope.revision === ticket.revision &&
    admission === ticket.admission &&
    (!ticket.owner || ticket.owner.isCurrent()) &&
    (admission === undefined || ticket.owner?.runId === admission)
  );
}

export function invalidateAskDecodaHistoryReads(params: ScopeParams): void {
  historyScope(params).revision += 1;
  // TanStack cancels the retryer synchronously, even if HTTP ignores abort.
  // Never revert to pre-fetch data over the following authoritative update.
  void params.queryClient.cancelQueries(
    {
      queryKey: askDecodaThreadHistoryQueryKey(
        params.tenantSubDomain,
        params.threadId,
      ),
      exact: true,
    },
    { revert: false },
  );
}

export function askDecodaHistoryQueryOptions(
  params: ScopeParams & { owner?: Owner },
) {
  const scope = historyScope(params);
  return queryOptions({
    queryKey: askDecodaThreadHistoryQueryKey(
      params.tenantSubDomain,
      params.threadId,
    ),
    staleTime: ASK_DECODA_THREAD_DETAIL_STALE_MS,
    structuralSharing: scope.structuralSharing,
    queryFn: async ({ signal }): Promise<ChatHistoryEntry[]> => {
      const state = useAskDecodaStore.getState();
      const ticket: Ticket = {
        scope,
        epoch: state.runtimeEpoch,
        revision: scope.revision,
        admission: state.liveRunsByThreadId[params.threadId]?.runId,
        owner: params.owner,
        signal,
      };
      const fetched = await getNativeAskDecodaHistory(params.threadId, {
        signal,
      });
      // API aborts can be swallowed as null -> []. Never treat those as a fresh
      // empty history. Recheck again in structuralSharing at the actual commit.
      if (
        !eligible(scope, params.threadId, ticket) &&
        params.queryClient.getQueryData(
          askDecodaThreadHistoryQueryKey(
            params.tenantSubDomain,
            params.threadId,
          ),
        ) === undefined
      ) {
        throw new CancelledError({ revert: false });
      }
      const incoming = [...fetched];
      readTickets.set(incoming, ticket);
      return incoming;
    },
  });
}
